import React, { Component } from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import CircularProgress from '@material-ui/core/CircularProgress';
import TextField from '@material-ui/core/TextField';
import InputAdornment from '@material-ui/core/InputAdornment';
import Chip from '@material-ui/core/Chip';
import Autocomplete from '@material-ui/lab/Autocomplete';
import Tabs from '@material-ui/core/Tabs';
import Tab from '@material-ui/core/Tab';
import Banner from '../../components/banner';
import ErrorBoundary from '../../components/error-boundary';
import Auth from '../../auth/auth';
import { loadObjects, searchObjects, loadObjectDetails, loadObjectRelated, loadObjectHistory, loadCategories, loadSchemas, setSelectedLetter, setCategoryFilter, setSchemaFilter, setSearchTerm } from './data-dictionary-view.actions';
import AlphabetBar from './components/alphabet-bar';
import ObjectTable from './components/object-table';
import ObjectDetailSheet from './components/object-detail-sheet';
import ReportsGrid from './components/reports-grid';
import PiiReport from './components/reports/pii-report';
import AiEligibleReport from './components/reports/ai-eligible-report';
import OwnershipReport from './components/reports/ownership-report';
import SchemaAuditReport from './components/reports/schema-audit-report';
import UsageAccessReport from './components/reports/usage-access-report';


const auth = new Auth();
let orgId;

const ALPHABET = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ#'.split('');

class DataDictionaryView extends Component {
  constructor(props) {
    super(props);
    this.state = {
      appContentContainer: 'app-content-container',
      pageContentContainer: 'page-content select-container in',
      showBanner: false,
      bannerType: 'danger',
      bannerTitle: '',
      bannerMessage: '',
      selectedObject: null,
      sheetOpen: false,
      searchValue: '',
      searchDebounceTimer: null,
      categoryValue: [],
      schemaValue: [],
      activeTab: 'catalog',
      activeReport: null,
    };
    orgId = auth.getOrgId();
  }

  componentDidMount = () => {
    // Load categories, schemas and objects
    this.props.loadCategories();
    this.props.loadSchemas();
    this.loadData();
  }

  componentDidUpdate(prevProps) {
    // Reload data when filters change
    const categoryChanged = JSON.stringify(prevProps.categoryFilter) !== JSON.stringify(this.props.categoryFilter);
    const schemaChanged = JSON.stringify(prevProps.schemaFilter) !== JSON.stringify(this.props.schemaFilter);
    
    if (
      prevProps.selectedLetter !== this.props.selectedLetter ||
      categoryChanged ||
      schemaChanged ||
      prevProps.searchTerm !== this.props.searchTerm
    ) {
      this.loadData();
    }
  }

  loadData = () => {
    const { selectedLetter, categoryFilter, schemaFilter, searchTerm } = this.props;
    
    // Use search API if search term is present
    if (searchTerm && searchTerm.trim().length >= 2) {
      // Pass categoryFilter and schemaFilter arrays directly (can be empty, one, or many)
      this.props.searchObjects(searchTerm, categoryFilter || [], schemaFilter || []);
    } else {
      // Use browse API for letter-based navigation
      this.props.loadObjects(selectedLetter, categoryFilter, schemaFilter);
    }
  }

  dismissBanner = () => {
    this.setState({ showBanner: false });
  }

  handleLetterClick = (letter) => {
    const { searchTerm } = this.props;
    if (searchTerm) {
      // Clear search when clicking letter
      this.setState({ searchValue: '' });
      this.props.setSearchTerm('');
    }
    const letterLower = letter.toLowerCase();
    this.props.setSelectedLetter(letterLower);
  }

  handleCategoryChange = (event, newValue) => {
    const categoryValues = newValue.map(cat => cat.value);
    this.setState({ categoryValue: newValue });
    this.props.setCategoryFilter(categoryValues);
  }

  handleSchemaChange = (event, newValue) => {
    const schemaIds = newValue.map(schema => schema.schemaId);
    this.setState({ schemaValue: newValue });
    this.props.setSchemaFilter(schemaIds);
  }

  handleSearchChange = (event) => {
    const value = event.target.value;
    this.setState({ searchValue: value });

    // Clear previous timer
    if (this.state.searchDebounceTimer) {
      clearTimeout(this.state.searchDebounceTimer);
    }

    // Set new timer for debounce
    const timer = setTimeout(() => {
      if (value.length >= 2 || value.length === 0) {
        this.props.setSearchTerm(value);
      }
    }, 300);

    this.setState({ searchDebounceTimer: timer });
  }

  handleSearchClear = () => {
    this.setState({ searchValue: '' });
    this.props.setSearchTerm('');
  }

  handleRowClick = (object) => {
    this.setState({ 
      selectedObject: object,
      sheetOpen: true 
    });
    // Load object details using the object's ID
    if (object.objectId) {
      this.props.loadObjectDetails(object.objectId);
    }
  }

  handleSheetClose = () => {
    this.setState({ 
      sheetOpen: false,
      selectedObject: null 
    });
  }

  handleNavigateObject = (direction) => {
    const { objects } = this.props;
    const { selectedObject } = this.state;
    
    if (!selectedObject || !objects || objects.length === 0) return;
    
    const currentIndex = objects.findIndex(obj => 
      (obj.objectId || obj.id) === (selectedObject.objectId || selectedObject.id)
    );
    
    if (currentIndex === -1) return;
    
    let newIndex;
    if (direction === 'prev') {
      newIndex = currentIndex > 0 ? currentIndex - 1 : objects.length - 1;
    } else {
      newIndex = currentIndex < objects.length - 1 ? currentIndex + 1 : 0;
    }
    
    const newObject = objects[newIndex];
    this.setState({ selectedObject: newObject });
    
    // Load details for the new object
    if (newObject.objectId) {
      this.props.loadObjectDetails(newObject.objectId);
    }
  }

  handleTabChange = (event, newTab) => {
    this.setState({ activeTab: newTab, activeReport: null });
  }

  handleSelectReport = (reportId) => {
    this.setState({ activeReport: reportId });
  }

  handleBackToReports = () => {
    this.setState({ activeReport: null });
  }



  render() {
    const { objects, categories, schemas, isLoading, objectDetails, selectedLetter, categoryFilter, searchTerm, availableLetters } = this.props;
    const { sheetOpen, selectedObject, searchValue, categoryValue, schemaValue, activeTab, activeReport } = this.state;
    
    const isLoadingObjects = isLoading.includes('objects');
    const isLoadingDetails = isLoading.includes('objectdetails');
    const isSearching = searchTerm && searchTerm.length >= 2;

    return (
      <div className={this.state.appContentContainer}>
        <Banner
          type={this.state.bannerType}
          hasAlerts={this.state.showBanner}
          header={this.state.bannerTitle}
          body={this.state.bannerMessage}
          dismissBanner={this.dismissBanner.bind(this)}
        />

        <div className={this.state.pageContentContainer}>
          <ErrorBoundary>
            {/* Top Bar */}
            <div className="dd-view-top-bar">
              <h2>Data Dictionary</h2>
            </div>

            {/* Tab Bar */}
            <div className="dd-view-tabs">
              <Tabs
                className="recon-tabs"
                value={activeTab}
                onChange={this.handleTabChange}
              >
                <Tab label="Catalog" value="catalog" />
                <Tab label="Reports" value="reports" />
              </Tabs>
            </div>

            {/* ── Catalog tab ─────────────────────────────────────────────── */}
            {activeTab === 'catalog' && (
              <>
                {/* Filter Bar */}
                <div className="dd-view-filter-bar">
                  <div className="dd-view-filter-row">
                    <Autocomplete
                      multiple
                      disableCloseOnSelect
                      filterSelectedOptions={true}
                      autoHighlight
                      options={Array.isArray(categories) ? categories : []}
                      getOptionLabel={option => option.label}
                      value={categoryValue}
                      onChange={this.handleCategoryChange}
                      renderTags={(value, getTagProps) =>
                        value.map((option, index) => (
                          <Chip label={option.label} {...getTagProps({ index })} />
                        ))
                      }
                      renderInput={params => (
                        <TextField
                          {...params}
                          variant="standard"
                          label="Category"
                          placeholder="Select categories"
                          margin="normal"
                          fullWidth
                        />
                      )}
                      className="dd-view-category-filter"
                    />

                    <Autocomplete
                      multiple
                      disableCloseOnSelect
                      filterSelectedOptions={true}
                      autoHighlight
                      options={Array.isArray(schemas) ? schemas : []}
                      getOptionLabel={option => `${option.instanceName} (${option.schemaName})`}
                      value={schemaValue}
                      onChange={this.handleSchemaChange}
                      renderTags={(value, getTagProps) =>
                        value.map((option, index) => (
                          <Chip label={`${option.instanceName} (${option.schemaName})`} {...getTagProps({ index })} />
                        ))
                      }
                      renderInput={params => (
                        <TextField
                          {...params}
                          variant="standard"
                          label="Schema"
                          placeholder="Select schemas"
                          margin="normal"
                          fullWidth
                        />
                      )}
                      className="dd-view-schema-filter"
                    />

                    <TextField
                      variant="standard"
                      label="Search"
                      placeholder="Search items..."
                      value={searchValue}
                      onChange={this.handleSearchChange}
                      margin="normal"
                      fullWidth
                      inputProps={{'data-testid': 'dd-search-input'}}
                      InputProps={{
                        endAdornment: searchValue && (
                          <InputAdornment position="end">
                            <i 
                              className="material-icons search-clear" 
                              onClick={this.handleSearchClear}
                            >
                              close
                            </i>
                          </InputAdornment>
                        )
                      }}
                      className="dd-view-search"
                    />
                  </div>
                </div>

                {/* Alphabetical Index Bar */}
                <AlphabetBar
                  selectedLetter={selectedLetter}
                  availableLetters={availableLetters}
                  disabled={isSearching}
                  onLetterClick={this.handleLetterClick}
                  itemCount={objects.length}
                />

                {/* Results Header */}
                {isSearching && (
                  <div className="dd-view-search-header">
                    Search results for "{searchTerm}" ({objects.length} items)
                  </div>
                )}

                {/* Data Table */}
                {isLoadingObjects ? (
                  <div className="dd-view-loading">
                    <CircularProgress />
                    <p>Loading items...</p>
                  </div>
                ) : objects.length === 0 ? (
                  <div className="dd-view-empty" data-testid="dd-empty">
                    <i className="material-icons">{isSearching ? 'search' : 'book'}</i>
                    <h3>{isSearching ? 'No results found' : 'No items found'}</h3>
                    <p>
                      {isSearching 
                        ? `No results for "${searchTerm}". Try adjusting your filters.`
                        : `No items start with "${selectedLetter.toUpperCase()}". Try a different letter or search.`}
                    </p>
                  </div>
                ) : (
                  <ObjectTable
                    objects={objects}
                    onRowClick={this.handleRowClick}
                    selectedObject={this.state.selectedObject}
                  />
                )}
              </>
            )}

            {/* ── Reports tab ──────────────────────────────────────────────── */}
            {activeTab === 'reports' && (
              <>
                {!activeReport && (
                  <ReportsGrid onSelectReport={this.handleSelectReport} />
                )}
                {activeReport === 'pii' && (
                  <PiiReport onBack={this.handleBackToReports} />
                )}
                {activeReport === 'aiEligible' && (
                  <AiEligibleReport onBack={this.handleBackToReports} />
                )}
                {activeReport === 'ownership' && (
                  <OwnershipReport onBack={this.handleBackToReports} />
                )}
                {activeReport === 'schemaAudit' && (
                  <SchemaAuditReport onBack={this.handleBackToReports} />
                )}
                {activeReport === 'usage' && (
                  <UsageAccessReport onBack={this.handleBackToReports} />
                )}
              </>
            )}
          </ErrorBoundary>
        </div>

        {/* Detail Sheet */}
        {sheetOpen && selectedObject && (
          <ObjectDetailSheet
            open={sheetOpen}
            object={selectedObject}
            details={objectDetails}
            isLoading={isLoadingDetails}
            objectRelated={this.props.objectRelated}
            isLoadingRelated={this.props.isLoadingRelated}
            onLoadRelated={(objectId) => this.props.loadObjectRelated(objectId)}
            objectHistory={this.props.objectHistory}
            isLoadingHistory={this.props.isLoadingHistory}
            onLoadHistory={(objectId) => this.props.loadObjectHistory(objectId)}
            onClose={this.handleSheetClose}
            onNavigate={this.handleNavigateObject}
            objects={objects}
          />
        )}
      </div>
    );
  }
}

const mapStateToProps = (state) => ({
  objects: state.dataDictionaryViewReducer.objects,
  categories: state.dataDictionaryViewReducer.categories,
  schemas: state.dataDictionaryViewReducer.schemas,
  isLoading: state.dataDictionaryViewReducer.isLoading,
  objectDetails: state.dataDictionaryViewReducer.objectDetails,
  error: state.dataDictionaryViewReducer.error,
  selectedLetter: state.dataDictionaryViewReducer.selectedLetter,
  categoryFilter: state.dataDictionaryViewReducer.categoryFilter,
  schemaFilter: state.dataDictionaryViewReducer.schemaFilter,
  searchTerm: state.dataDictionaryViewReducer.searchTerm,
  availableLetters: state.dataDictionaryViewReducer.availableLetters,
  objectRelated: state.dataDictionaryViewReducer.objectRelated,
  isLoadingRelated: state.dataDictionaryViewReducer.isLoadingRelated,
  objectHistory: state.dataDictionaryViewReducer.objectHistory,
  isLoadingHistory: state.dataDictionaryViewReducer.isLoadingHistory
});

const mapDispatchToProps = {
  loadObjects,
  searchObjects,
  loadObjectDetails,
  loadObjectRelated,
  loadObjectHistory,
  loadCategories,
  loadSchemas,
  setSelectedLetter,
  setCategoryFilter,
  setSchemaFilter,
  setSearchTerm
};

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(DataDictionaryView));
